import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import LazyLoad from 'react-lazyload'
import Head from 'next/head'
import trackError from '@helpers/trackError'
import {
  isBrowser,
  getLink,
  getCurrencyCode,
  getShopLocale
} from '@helpers/env'
import { getValueFromObject } from '@helpers/utils'
import CustomHead from '@components/Head'
import { SizingWidgetData } from '@helpers/products/sizingWidgetData'
import { PRODUCTION_PATH, STAGING_PATH } from '@consts/index'
import {
  Product,
  ProductByType,
  ProductMeta,
  ProductVariant
} from 'types/product'
import {
  BenefitsProps,
  ContentfulDocument,
  StatementSectionProps
} from 'types/contentful/graphQlApi'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import styles from '../styles.module.scss'
import ProductContext from '../context'
import ProductContainer from '../index'
import { getProductInventoryByProductType } from '@api/inventory'
import TrustpilotReviews from '../TrustpilotReviews'
import trustpilotStyles from '../TrustpilotReviews/styles.module.scss'
import {
  TrustpilotRatings,
  TrustpilotReview,
  REVIEWS_DISPLAY_THRESHOLD
} from '@api/trustpilot'
import { getProductsWithUpdatedVariants } from '@helpers/filters/updateVariants'
import StatementSection from '@components/pages/components/StatementSection'
import Benefits from '@components/ui/Benefits'
import PageSection from '@components/ui/PageSection'
import { EmptyObject } from 'types/helpers'
import { BundleProvider } from '../mto/BundleContext'
import {
  DiscountByProductType,
  productHasDiscountTag
} from '@lib/productPageUtils'

const SizingWidget = dynamic(() => import('../SizingWidget'))
const ProductProductsBreakdown = dynamic(
  () => import('../ProductProductsBreakdown')
)
const Loader = dynamic(() => import('../../Loading/PageLoading'))

const basePath =
  process.env.API_ENV === 'production' ? PRODUCTION_PATH : STAGING_PATH

function makeProductSchema(
  product: ProductListProduct,
  productUrl: string,
  meta: { description: string },
  ratings: TrustpilotRatings | null
) {
  return {
    '@context': 'http://schema.org',
    '@type': 'Product',
    description: meta.description,
    name: product.product_type,
    image: product.image?.src,
    brand: product.product_feed?.brand,
    sku: product.product_feed?.product_id,
    mpn: product.product_feed?.product_id,
    color: product.title,
    offers: {
      '@type': 'Offer',
      url: productUrl,
      priceCurrency: getCurrencyCode(),
      price: Number(product.price) || null,
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      hasMerchantReturnPolicy: {
        '@type': 'MerchantReturnPolicy',
        returnPolicyCategory:
          'https://schema.org/MerchantReturnFiniteReturnWindow',
        merchantReturnDays: 200,
        returnMethod: 'https://schema.org/ReturnByMail',
        returnFees: 'https://schema.org/FreeReturn'
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: getShopLocale() === 'US' ? '15.00' : '4.95',
          currency: getCurrencyCode()
        },
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: getShopLocale()
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 2,
            unitCode: 'd'
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 2,
            maxValue: 4,
            unitCode: 'd'
          }
        }
      }
    },
    ...(ratings && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: ratings.starsAverage,
        ratingCount: ratings.numberOfReviews.total
      }
    })
  }
}

type ProductDiscount = {
  title: string
  description: string
}

export type ProductListProduct = Product & {
  discount: ProductDiscount
  byline: string
  variants?: ProductVariant[]
}

export type ProductsList = (ProductByType & {
  products: ProductListProduct[]
})[]

type SizeChartAvailabilityByProductType = Record<string, boolean>

export type ProductListProps = {
  productsList: ProductsList
  productsBreakdown: ProductsBreakdownProps | null
  meta: ProductMeta
  careCopy: ContentfulDocument | null
  productIds: number[]
  productTypes: string[]
  productStatementSection: StatementSectionProps | null
  productBenefits: BenefitsProps | null
  discountByProductType: DiscountByProductType | null
  sizingWidgetData: SizingWidgetData | EmptyObject
  sizeChartAvailabilityByProductType?: SizeChartAvailabilityByProductType
  trustpilotRatings?: TrustpilotRatings | null
  trustpilotReviews?: TrustpilotReview[]
  trustpilotDrawerReviews?: TrustpilotReview[]
}

const ProductList = ({
  productsBreakdown = null,
  careCopy,
  productsList,
  productBenefits,
  productStatementSection,
  discountByProductType,
  sizingWidgetData,
  sizeChartAvailabilityByProductType = {},
  trustpilotRatings = null,
  trustpilotReviews = [],
  trustpilotDrawerReviews = []
}: ProductListProps) => {
  const { query, asPath } = useRouter()

  const meetsThreshold =
    process.env.API_ENV === 'production'
      ? trustpilotRatings !== null &&
        trustpilotRatings.numberOfReviews.total >= REVIEWS_DISPLAY_THRESHOLD
      : trustpilotRatings !== null
  const ratingsToShow =
    meetsThreshold && trustpilotReviews.length > 0 ? trustpilotRatings : null
  const productHandle = Array.isArray(query.productHandle)
    ? query.productHandle[0]
    : query.productHandle

  const [variantExist, setVariantExist] = useState(false)
  const [products, setProducts] = useState(productsList)
  const [drawerOpen, setDrawerOpen] = useState(false)

  const productUrl = `${basePath}${asPath}`

  const flatProducts = products.flatMap(product => product.products)
  const selectedProduct =
    flatProducts.find(product => {
      return product.handle.toLowerCase() === productHandle
    }) || flatProducts[0]
  const selectedProductHasSizeChart =
    sizeChartAvailabilityByProductType[selectedProduct?.product_type || ''] ||
    false

  const productContext = useMemo(() => {
    const selectedProductHasDiscount =
      productHasDiscountTag(selectedProduct?.tags) || false
    const hasPreviewDiscount =
      selectedProductHasDiscount &&
      !!selectedProduct?.product_type &&
      !!discountByProductType &&
      Object.prototype.hasOwnProperty.call(
        discountByProductType,
        selectedProduct.product_type
      )
    const discount = (
      hasPreviewDiscount
        ? discountByProductType?.[selectedProduct.product_type]
        : selectedProduct?.discount
    ) as Partial<ProductDiscount> | undefined

    return {
      discount: {
        title: discount?.title,
        description: discount?.description
      },
      careCopy,
      tabs: products,
      variantExist
    }
  }, [
    discountByProductType,
    careCopy,
    products,
    selectedProduct?.discount,
    selectedProduct?.product_type,
    selectedProduct?.tags,
    variantExist
  ])

  const hasFetchedVariants = useRef(false)
  useEffect(() => {
    // Fetch inventory once on mount.
    if (hasFetchedVariants.current) return
    hasFetchedVariants.current = true

    const fetchInventoryByProductTypes = async () => {
      const productTypes = products.map(
        product => product.products[0].product_type
      )

      const promises = productTypes.map(productType =>
        getProductInventoryByProductType(productType)
      )
      const responses = await Promise.all(promises)

      const inventoryItems = responses.flatMap(
        inventory => inventory.productInventoryByType.items
      )

      return { productInventoryByType: { items: inventoryItems } }
    }

    const fetchVariants = async () => {
      try {
        const { productInventoryByType } = await fetchInventoryByProductTypes()

        const productsWithVariants = products.map(productType => ({
          ...productType,
          products:
            getProductsWithUpdatedVariants(
              productType.products,
              productInventoryByType.items
            ) || []
        }))
        setProducts(productsWithVariants)
        setVariantExist(true)
      } catch (e) {
        const variantsError = new Error('Product fetchVariants failed')
        const error = e instanceof Error ? e : variantsError
        trackError(variantsError, { error })
      }
    }
    fetchVariants()
  }, [products])

  const shouldShowProductsBreakdown =
    productsBreakdown && productsBreakdown?.categories.items.length > 0
  const shouldShowSizingWidget = Object.keys(sizingWidgetData).length > 0

  if (query && !selectedProduct) {
    if (Object.keys(query).length === 0 && isBrowser()) {
      global.window.location.href = getLink('/pages/404')
    }
    return <Loader />
  }

  const metaSeo = {
    title:
      getValueFromObject(selectedProduct, 'meta.seo_title', undefined) ||
      `${selectedProduct?.title} - ${selectedProduct?.product_type}`,
    description:
      getValueFromObject(selectedProduct, 'meta.seo_description', undefined) ||
      selectedProduct?.byline,
    image: {
      url: selectedProduct?.image?.src,
      width: selectedProduct?.image?.width,
      height: selectedProduct?.image?.height
    }
  }

  return (
    <div className={styles.productContainer}>
      <Head>
        <script
          key={`jobJSON-${selectedProduct?.id}`}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(
              makeProductSchema(
                selectedProduct,
                productUrl,
                metaSeo,
                ratingsToShow
              )
            )
          }}
        />
      </Head>
      <CustomHead
        title={metaSeo.title}
        desc={metaSeo.description}
        image={metaSeo.image}
        includeNoFollow={false}
      />
      <ProductContext.Provider value={productContext}>
        <BundleProvider>
          <ProductContainer
            selectedProduct={selectedProduct}
            byline={selectedProduct?.byline || ''}
            productHandle={productHandle || ''}
            productColor={selectedProduct?.title || ''}
            variantExist={variantExist}
            hasSizeChartData={selectedProductHasSizeChart}
            trustpilotRatings={ratingsToShow}
            onOpenDrawer={
              ratingsToShow && trustpilotDrawerReviews.length > 0
                ? () => setDrawerOpen(true)
                : undefined
            }
          />
        </BundleProvider>
      </ProductContext.Provider>
      {productStatementSection && (
        <PageSection>
          <StatementSection {...productStatementSection} />
        </PageSection>
      )}
      {productBenefits && (
        <PageSection>
          <Benefits
            backgroundColor={productBenefits.backgroundColor}
            benefits={productBenefits.benefits}
          />
        </PageSection>
      )}
      {shouldShowSizingWidget && (
        <PageSection>
          <SizingWidget
            sizingWidgetData={sizingWidgetData}
            productType={selectedProduct.product_type || ''}
            productCategory={selectedProduct?.product_feed?.category || ''}
          />
        </PageSection>
      )}
      {shouldShowProductsBreakdown && (
        <LazyLoad once>
          <PageSection>
            <ProductProductsBreakdown
              productsBreakdown={productsBreakdown}
              productOptions={selectedProduct?.options || []}
              productType={selectedProduct?.product_type || ''}
            />
          </PageSection>
        </LazyLoad>
      )}
      {ratingsToShow && trustpilotReviews.length > 0 && (
        <PageSection
          id="trustpilot-reviews"
          classes={trustpilotStyles.TrustpilotReviews__anchor}
        >
          <TrustpilotReviews
            ratings={ratingsToShow}
            reviews={trustpilotReviews}
            drawerReviews={trustpilotDrawerReviews}
            productType={selectedProduct?.product_type || ''}
            isDrawerOpen={drawerOpen}
            onOpenDrawer={() => setDrawerOpen(true)}
            onCloseDrawer={() => setDrawerOpen(false)}
          />
        </PageSection>
      )}
    </div>
  )
}

export default ProductList
